define('drops/audioPlayer/audioPlayerBase',["underscore"], function (_) {
    "use strict";

    function AudioPlayerBase(drop) {
        this.drop = drop;
        this.$audio = null;
        this.audioElement = null;
        this.lastUnmutedVolume = 1.0;
        this.lastMutedState = null;
        this.lastReportedVolume = null;
        this.pendingSeekTime = null;
        this.playbackRates =
            this.drop && typeof this.drop.getPlaybackRates === "function"
                ? this.drop.getPlaybackRates()
                : [1.0, 1.25, 1.5, 2.0];
        this.currentRateIndex = this.getDefaultPlaybackRateIndex();
        this.lastReportedRate = this.getDefaultPlaybackRate();
    }

    AudioPlayerBase.prototype.triggerEvent = function (eventName, arg) {
        if (this.drop && typeof this.drop.triggerEventIfEnabled === "function") {
            this.drop.triggerEventIfEnabled(eventName, arg);
        }
    };

    AudioPlayerBase.prototype.createAudioElement = function () {
        return null;
    };

    AudioPlayerBase.prototype.bindAudioEvents = function () {
        var self = this;
        if (!this.$audio) return;

        this.$audio.off(".audioplayer");

        this.$audio.on("play.audioplayer", function () {
            self.onPlay();
            self.triggerEvent("audioPlay");
        });

        this.$audio.on("pause.audioplayer", function () {
            self.onPause();
            self.triggerEvent("audioPause");
        });

        this.$audio.on("ended.audioplayer", function () {
            self.onEnded();
            self.triggerEvent("audioEnded");
        });

        this.$audio.on("timeupdate.audioplayer", function () {
            var curr = self.audioElement ? self.audioElement.currentTime : 0;
            self.onTimeUpdate(curr);
        });

        this.$audio.on("loadedmetadata.audioplayer durationchange.audioplayer", function () {
            var dur = self.audioElement ? self.audioElement.duration : 0;
            self.onDurationChange(dur);
            if (self.audioElement) {
                var currentRate = self.playbackRates[self.currentRateIndex] || 1.0;
                if (self.audioElement.playbackRate !== currentRate) {
                    self.audioElement.playbackRate = currentRate;
                }

                if (self.pendingSeekTime !== null && self.pendingSeekTime !== undefined) {
                    var target = self.pendingSeekTime;
                    self.pendingSeekTime = null;
                    self.seekTo(target);
                }
            }
        });

        this.$audio.on("ratechange.audioplayer", function () {
            if (self.audioElement) {
                var rate = self.audioElement.playbackRate;
                if (self.lastReportedRate === rate) {
                    return;
                }
                self.lastReportedRate = rate;
                var idx = self.playbackRates.indexOf(rate);
                if (idx !== -1) {
                    self.currentRateIndex = idx;
                }
                self.onRateChange(rate);
                self.triggerEvent("speedChanged", rate);
            }
        });
    };

    // UI Lifecycle hooks (to be optionally overridden by subclasses)
    AudioPlayerBase.prototype.onPlay = function () {};
    AudioPlayerBase.prototype.onPause = function () {};
    AudioPlayerBase.prototype.onEnded = function () {};
    AudioPlayerBase.prototype.onTimeUpdate = function (/* curr */) {};
    AudioPlayerBase.prototype.onDurationChange = function (/* dur */) {};
    AudioPlayerBase.prototype.onVolumeChange = function (/* vol, isMuted */) {};
    AudioPlayerBase.prototype.onRateChange = function (/* rate */) {};
    AudioPlayerBase.prototype.onSeeked = function (/* curr */) {};

    // Playback rate management
    AudioPlayerBase.prototype.getDefaultPlaybackRateIndex = function () {
        if (!this.playbackRates || !this.playbackRates.length) return 0;

        var idx = this.playbackRates.indexOf(1.0);
        if (idx === -1) idx = this.playbackRates.indexOf(1);

        return idx !== -1 ? idx : 0;
    };

    AudioPlayerBase.prototype.getDefaultPlaybackRate = function () {
        if (!this.playbackRates || !this.playbackRates.length) return 1.0;

        var idx = this.getDefaultPlaybackRateIndex();
        return this.playbackRates[idx] || 1.0;
    };

    AudioPlayerBase.prototype.setPlaybackRate = function (rate) {
        if (!this.ensureAudioLoaded()) return;

        var num = parseFloat(rate);
        if (isNaN(num) || num <= 0) return;

        var idx = this.playbackRates.indexOf(num);
        if (idx !== -1) {
            this.currentRateIndex = idx;
        }

        if (this.audioElement.playbackRate !== num) {
            this.audioElement.playbackRate = num;
        } else {
            this.onRateChange(num);
        }
    };

    AudioPlayerBase.prototype.cyclePlaybackRate = function () {
        if (!this.ensureAudioLoaded()) return;
        if (!this.playbackRates || !this.playbackRates.length) return;

        this.currentRateIndex = (this.currentRateIndex + 1) % this.playbackRates.length;
        var rate = this.playbackRates[this.currentRateIndex];

        if (this.audioElement.playbackRate !== rate) {
            this.audioElement.playbackRate = rate;
        } else {
            this.onRateChange(rate);
        }
    };

    // Playback Controls
    AudioPlayerBase.prototype.playAudio = function () {
        if (!this.ensureAudioLoaded()) return;

        var playPromise = this.audioElement.play();
        if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch(function (err) {
                console.warn("Audio play error:", err);
            });
        }

        var curr = this.audioElement ? this.audioElement.currentTime : 0;
        this.triggerEvent("playPauseClicked", curr);
    };

    AudioPlayerBase.prototype.pauseAudio = function () {
        if (!this.audioElement) return;

        this.audioElement.pause();
        var curr = this.audioElement ? this.audioElement.currentTime : 0;
        this.triggerEvent("playPauseClicked", curr);
    };

    AudioPlayerBase.prototype.togglePlayPause = function () {
        if (!this.ensureAudioLoaded()) return;

        if (this.audioElement.paused) {
            this.playAudio();
        } else {
            this.pauseAudio();
        }
    };

    // Skip Controls
    AudioPlayerBase.prototype.getSkipTimeSeconds = function () {
        var ms = parseInt(this.drop.data && this.drop.data.skipTimeMs, 10);
        if (isNaN(ms) || ms <= 0) ms = 10000;

        return ms / 1000;
    };

    AudioPlayerBase.prototype.skip = function (seconds) {
        if (!this.ensureAudioLoaded()) return;

        var dur = this.audioElement.duration || 0;
        var target = Math.max(0, this.audioElement.currentTime + seconds);
        if (dur > 0) {
            target = Math.min(dur, target);
        }
        this.audioElement.currentTime = target;

        this.onSeeked(target);

        var eventName = seconds < 0 ? "skipBack" : "skipForward";
        this.triggerEvent(eventName, target);
    };

    AudioPlayerBase.prototype.seekTo = function (seconds) {
        var target = Math.max(0, Number(seconds) || 0);
        if (!this.audioElement || isNaN(this.audioElement.duration) || this.audioElement.duration === 0) {
            this.pendingSeekTime = target;
            this.ensureAudioLoaded();
            return;
        }

        var dur = this.audioElement.duration;
        if (dur && dur > 0) {
            target = Math.min(dur, target);
        }
        this.audioElement.currentTime = target;
        this.onSeeked(target);
    };

    // Volume and Mute Controls
    AudioPlayerBase.prototype.getVolume = function () {
        if (this.audioElement) {
            if (this.audioElement.muted) return 0;
            return Math.round(this.audioElement.volume * 100);
        }
        return 100;
    };

    AudioPlayerBase.prototype.applyVolume = function (vol) {
        var num = Number(vol);
        if (isNaN(num)) return null;
        var normalized = Math.max(0, Math.min(1, num / 100));

        if (this.audioElement) {
            if (normalized === 0) {
                if (this.audioElement.volume > 0) {
                    this.lastUnmutedVolume = this.audioElement.volume;
                }
                this.audioElement.volume = 0;
                this.audioElement.muted = true;
            } else {
                this.lastUnmutedVolume = normalized;
                this.audioElement.volume = normalized;
                this.audioElement.muted = false;
            }
        }

        var isMuted = this.audioElement ? this.audioElement.muted || this.audioElement.volume === 0 : normalized === 0;
        var displayVol = Math.round(normalized * 100);

        this.onVolumeChange(displayVol, isMuted);
        return displayVol;
    };

    AudioPlayerBase.prototype.setVolume = function (vol) {
        var displayVal = this.applyVolume(vol);
        if (displayVal !== null) {
            this.triggerEvent("volumeChanged", displayVal);
        }
    };

    AudioPlayerBase.prototype.setMuted = function (isMuted) {
        if (!this.audioElement) return;

        var wasMuted = this.audioElement.muted || this.audioElement.volume === 0;
        var willBeMuted = !!isMuted;

        if (willBeMuted) {
            if (this.audioElement.volume > 0) {
                this.lastUnmutedVolume = this.audioElement.volume;
            }
            this.audioElement.muted = true;
        } else {
            this.audioElement.muted = false;
            if (this.audioElement.volume === 0) {
                this.audioElement.volume = this.lastUnmutedVolume > 0 ? this.lastUnmutedVolume : 1.0;
            }
        }

        var currentVol = willBeMuted ? 0 : Math.round(this.audioElement.volume * 100);
        this.onVolumeChange(currentVol, willBeMuted);

        if (wasMuted !== willBeMuted) {
            this.triggerEvent("muteToggled", willBeMuted);
            this.triggerEvent("volumeChanged", currentVol);
        }
    };

    AudioPlayerBase.prototype.toggleMute = function () {
        if (this.audioElement) {
            var isCurrentlyMuted = this.audioElement.muted || this.audioElement.volume === 0;
            this.setMuted(!isCurrentlyMuted);
        }
    };

    // State & Visibility
    AudioPlayerBase.prototype.ensureAudioLoaded = function () {
        var url = this.drop.getAudioUrl();
        if (!url) return false;

        if (!this.audioElement) {
            this.createAudioElement();
        }
        if (!this.audioElement) return false;

        var currentSrc = this.$audio.attr("src") || "";
        if (currentSrc !== url) {
            this.audioElement.pause();
            this.$audio.attr("src", url);
            this.audioElement.load();
            this.resetPlayerState(true);

            var initialSeek = this.drop && typeof this.drop.getInitialSkipToTimestampSeconds === "function"
                ? this.drop.getInitialSkipToTimestampSeconds()
                : 0;
            if (initialSeek > 0) {
                this.pendingSeekTime = initialSeek;
            }
        }
        return true;
    };

    AudioPlayerBase.prototype.afterShown = function () {
        if (this.drop.data.autoPlay && this.audioElement && this.audioElement.paused) {
            var playPromise = this.audioElement.play();
            if (playPromise && typeof playPromise.catch === "function") {
                playPromise.catch(function () {});
            }
        }
    };

    AudioPlayerBase.prototype.afterHidden = function () {
        if (this.audioElement && !this.audioElement.paused) {
            this.audioElement.pause();
        }
    };

    AudioPlayerBase.prototype.resetPlayerState = function (/* resetDuration */) {
        this.currentRateIndex = this.getDefaultPlaybackRateIndex();
        var defaultRate = this.getDefaultPlaybackRate();
        this.lastReportedRate = defaultRate;
        if (this.audioElement) {
            this.audioElement.currentTime = 0;
            this.audioElement.playbackRate = defaultRate;
        }
    };

    AudioPlayerBase.prototype.setAudioUrl = function (url) {
        if (!url) {
            this.clearValue();
            return;
        }

        if (this.$audio || this.drop.isVisible()) {
            if (this.ensureAudioLoaded()) {
                if (this.drop.data.autoPlay && this.drop.isVisible()) {
                    var playPromise = this.audioElement.play();
                    if (playPromise && typeof playPromise.catch === "function") {
                        playPromise.catch(function () {});
                    }
                }
            }
        }
    };

    AudioPlayerBase.prototype.clearValue = function () {
        if (this.$audio) {
            this.$audio.attr("src", "");
        }

        if (this.audioElement) {
            this.audioElement.pause();
            this.audioElement.removeAttribute("src");
            try {
                this.audioElement.load();
            } catch (e) {}
        }

        this.resetPlayerState(true);
    };

    AudioPlayerBase.prototype.dispose = function () {
        if (this.audioElement) {
            this.audioElement.pause();
            this.audioElement.removeAttribute("src");
            try {
                this.audioElement.load();
            } catch (e) {}
        }

        if (this.$audio) {
            this.$audio.off();
            this.$audio.remove();
            this.$audio = null;
        }

        this.audioElement = null;
    };

    return AudioPlayerBase;
});

